import { MeshBuilder, StandardMaterial, TransformNode, type Mesh } from "@babylonjs/core";
import { Btn, HeroId, Slot, fxk, hv, pk } from "@rivalwatch/shared";
import * as loops from "../../audio/cues/loops.ts";
import { bell, brass, click, thump, whoosh } from "../../audio/synth.ts";
import type { Out } from "../../audio/engine.ts";
import type { ClientHero, FxCtx, ProjectileFrame } from "../types.ts";

const V = { barrierUp: 0, barrierHp: 1, barrierRegenDelay: 2, rally: 3, combo: 4, dash: 5 } as const;
const PK = { Shield: pk(HeroId.CaptainAmericano, 0) } as const;
const FX = { Shield: fxk(HeroId.CaptainAmericano, 0), Rally: fxk(HeroId.CaptainAmericano, 1) } as const;

const shieldCue = (o: Out) => {
  click(o, 0.55, 2600, 0.004);
  thump(o, 0.8, 140, 58, 0.12);
  whoosh(o, 0.52, 500, 2300, 0.18);
  return 0.32;
};
const guardCue = (o: Out) => {
  thump(o, 0.72, 95, 44, 0.18);
  bell(o, 440, 0.26, 0.28);
  return 0.45;
};
const rallyCue = (o: Out) => {
  brass(o, 146.83, 0.8, 0.5);
  brass(o, 220, 0.8, 0.45);
  thump(o, 0.7, 72, 36, 0.45);
  return 1;
};

const theme = {
  bpm: 118,
  octave: 0,
  voice: brass,
  notes: [
    [0, 0.5],
    [7, 0.5],
    [12, 0.75],
    [9, 0.25],
    [7, 0.5],
    [12, 1],
  ],
} satisfies ClientHero["audio"]["theme"];

const hero: ClientHero<{ shield: Mesh; rim: Mesh; inner: Mesh; core: Mesh }> = {
  id: HeroId.CaptainAmericano,
  bulk: 1.18,
  shape: { torso: 1.12, arms: 1.08, legs: 1.06 },
  gear(g) {
    const { scene, rig, add, toon, glow } = g;
    const shield = add(MeshBuilder.CreateCylinder("americano-shield", { diameter: 0.96, height: 0.11, tessellation: 56 }, scene), rig.handL, toon("#1f57b7"));
    shield.rotation.x = Math.PI / 2;
    shield.position.set(-0.08, -0.02, -0.28);
    const rim = add(MeshBuilder.CreateTorus("americano-shield-rim", { diameter: 0.98, thickness: 0.055, tessellation: 56 }, scene), rig.handL, glow("#f8f7ed", 0.85));
    rim.rotation.x = Math.PI / 2;
    rim.position.copyFrom(shield.position);
    const inner = add(MeshBuilder.CreateTorus("americano-shield-red-ring", { diameter: 0.68, thickness: 0.045, tessellation: 48 }, scene), rig.handL, toon("#d92b32"));
    inner.rotation.x = Math.PI / 2;
    inner.position.copyFrom(shield.position);
    const core = add(MeshBuilder.CreateCylinder("americano-shield-cafe-core", { diameter: 0.24, height: 0.125, tessellation: 24 }, scene), rig.handL, glow("#f4d08a", 0.8));
    core.rotation.x = Math.PI / 2;
    core.position.copyFrom(shield.position);
    const stripe = add(MeshBuilder.CreateBox("americano-cafe-band", { width: 0.72, height: 0.03, depth: 0.18 }, scene), rig.torso, toon("#f8f7ed"));
    stripe.position.set(0, 0.18, -0.32);
    const cup = add(MeshBuilder.CreateCylinder("americano-cup-mark", { diameterTop: 0.2, diameterBottom: 0.28, height: 0.2, tessellation: 10 }, scene), rig.torso, toon("#d92b32"));
    cup.position.set(0, 0.04, -0.35);
    return { shield, rim, inner, core };
  },
  animate(gear, f) {
    const raised = f.v[V.barrierUp] > 0.5;
    const rally = f.v[V.rally] > 0;
    const z = raised ? -0.58 : -0.28;
    for (const part of [gear.shield, gear.rim, gear.inner, gear.core]) {
      part.rotation.y = raised ? Math.PI * 0.5 : 0;
      part.position.z = z;
      part.scaling.setAll(rally ? 1.08 + Math.sin(f.t * 16) * 0.04 : 1);
    }
    if (f.action === 4 || f.action === 5) return "holding-both";
    if (raised) return "holding-left";
    if (f.action === 1 || f.action === 2) return "attack-melee-left";
  },
  hud(c, eid) {
    return [
      { label: "Shield", value: hv(c, eid, V.barrierHp) / 350, color: "#f8f7ed" },
      { label: "Rally", value: hv(c, eid, V.rally) / 600, color: "#2f6fe0" },
    ];
  },
  lifting(c, eid, buttons, grounded) {
    return grounded && hv(c, eid, V.dash) > 0 && !!(buttons & Btn.Jump);
  },
  projectiles: {
    [PK.Shield](scene, _f: ProjectileFrame) {
      const root = new TransformNode("americano-shield-fly-root", scene);
      const blue = new StandardMaterial("americano-shield-fly-blue", scene);
      blue.diffuseColor.set(0.08, 0.24, 0.62);
      blue.emissiveColor.set(0.03, 0.08, 0.16);
      const white = new StandardMaterial("americano-shield-fly-white", scene);
      white.diffuseColor.set(0.95, 0.95, 0.9);
      white.emissiveColor.set(0.18, 0.18, 0.18);
      const red = new StandardMaterial("americano-shield-fly-red", scene);
      red.diffuseColor.set(0.8, 0.08, 0.12);
      red.emissiveColor.set(0.16, 0.03, 0.03);
      const disc = MeshBuilder.CreateCylinder("americano-shield-fly-disc", { diameter: 0.78, height: 0.1, tessellation: 48 }, scene);
      const rim = MeshBuilder.CreateTorus("americano-shield-fly-rim", { diameter: 0.82, thickness: 0.05, tessellation: 48 }, scene);
      const ring = MeshBuilder.CreateTorus("americano-shield-fly-ring", { diameter: 0.55, thickness: 0.04, tessellation: 48 }, scene);
      const core = MeshBuilder.CreateCylinder("americano-shield-fly-core", { diameter: 0.2, height: 0.12, tessellation: 20 }, scene);
      for (const m of [disc, rim, ring, core]) {
        m.parent = root;
        m.rotation.x = Math.PI / 2;
      }
      disc.material = blue;
      rim.material = white;
      ring.material = red;
      core.material = white;
      return {
        gear: root,
        update(m: TransformNode, frame) {
          m.position.set(frame.pos.x, frame.pos.y, frame.pos.z);
          m.rotation.set(0, frame.age * 28, Math.atan2(frame.dir.x, frame.dir.z));
        },
        dispose() {
          root.dispose(false, true);
          blue.dispose();
          white.dispose();
          red.dispose();
        },
      };
    },
  },
  fx: {
    [FX.Shield]: {
      impact(c: FxCtx, pos) {
        const ring = MeshBuilder.CreateTorus("americano-impact", { diameter: 1.1, thickness: 0.035, tessellation: 32 }, c.scene);
        ring.position.set(pos.x, pos.y, pos.z);
        ring.material = c.glow("#f8f7ed", 0.85);
        c.timed(ring, 0.28, (t, m) => m.scaling.setAll(1 + t * 1.8));
      },
    },
    [FX.Rally]: {
      explosion(c: FxCtx, pos, radius) {
        const ring = MeshBuilder.CreateTorus("americano-rally", { diameter: radius * 2, thickness: 0.08, tessellation: 64 }, c.scene);
        ring.position.set(pos.x, pos.y + 0.05, pos.z);
        ring.material = c.glow("#2f6fe0", 0.45);
        c.timed(ring, 0.7, (t, m) => {
          m.scaling.setAll(0.25 + t);
          m.rotation.y += 0.08;
        });
      },
      keepGeneric: true,
    },
  },
  audio: {
    weight: "heavy",
    fire: () => ({ key: "fire:captain-americano:shield", cue: shieldCue }),
    ability: {
      [Slot.Secondary]: guardCue,
      [Slot.Ability1]: shieldCue,
      [Slot.Ability2]: guardCue,
      [Slot.Ability3]: shieldCue,
      [Slot.Ultimate]: rallyCue,
    },
    theme,
    themeTrim: { heroic: 0, hostile: -7, tease: -1 },
    loops: ({ v, alive }) => {
      const out = [];
      if (alive && v[V.barrierUp] > 0.5) out.push({ key: "guard", build: loops.ward, gain: 0.7, value: v[V.barrierHp] / 350 });
      if (alive && v[V.rally] > 0) out.push({ key: "rally", build: loops.visor, gain: 0.45 });
      return out;
    },
    flight: { [PK.Shield]: loops.flight("hook") },
    impacts: { [FX.Shield]: shieldCue },
    explosions: { [FX.Rally]: () => rallyCue },
  },
  icons: {
    [Slot.Primary]: "M6 16c2 3 8 3 10 0M8 13l-2-2 2-2M16 13l2-2-2-2",
    [Slot.Secondary]: "M12 3l7 3v5c0 4-3 7-7 9-4-2-7-5-7-9V6z",
    [Slot.Ability1]: "M4 15l6-6M9 20l10-10M5 6h8l6 6",
    [Slot.Ability2]: ["M12 4a8 8 0 1 0 0 16 8 8 0 0 0 0-16z", "M12 8v8M8 12h8"],
    [Slot.Ability3]: "M4 12h13M13 7l5 5-5 5M5 6l3 6-3 6",
    [Slot.Ultimate]: "M4 18c4-8 12-8 16 0M12 4v12M8 8l4-4 4 4",
    passive: "M12 3l2.5 5 5.5.8-4 3.9 1 5.5-5-2.6-5 2.6 1-5.5-4-3.9 5.5-.8z",
  },
};

export default hero;
